import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { getAgChartTheme, getChartColors } from '../../../analytics/charts/chartTheme';
import { useTheme } from '../../../../stores/ThemeContext';
import { ShieldCheck } from 'lucide-react';

const RuleAdherenceChart = ({ data = [], loading }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 320 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height: 320 });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const colors = useMemo(() => getChartColors(isDark), [isDark]);

  // Filter to rules that have at least one data point
  const chartData = useMemo(() =>
    data
      .filter((r) => r.followed_count > 0 || r.broken_count > 0)
      .map((r) => ({
        label: r.label,
        followed: r.followed_avg_pnl,
        broken: r.broken_avg_pnl,
        follow_rate: r.follow_rate_pct,
      })),
  [data]);

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: chartData,
    series: [
      {
        type: 'bar',
        xKey: 'label',
        yKey: 'followed',
        yName: 'Followed — Avg PnL',
        fill: colors.win,
        stroke: colors.win,
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.label,
            content: `Followed avg PnL: ${datum.followed != null ? `$${datum.followed.toFixed(2)}` : '—'}`,
          }),
        },
      },
      {
        type: 'bar',
        xKey: 'label',
        yKey: 'broken',
        yName: 'Broken — Avg PnL',
        fill: colors.loss,
        stroke: colors.loss,
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.label,
            content: `Broken avg PnL: ${datum.broken != null ? `$${datum.broken.toFixed(2)}` : '—'}`,
          }),
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          fontSize: 10,
          rotation: -40,
        },
      },
      {
        type: 'number',
        position: 'left',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          formatter: ({ value }) => `$${value.toFixed(0)}`,
          fontSize: 10,
        },
      },
    ],
    legend: {
      enabled: true,
      position: 'top',
    },
  }), [chartData, dimensions, isDark, colors]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="h-72 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-muted-foreground">Rule Adherence vs Outcome</h3>
      </div>
      <p className="text-xs text-muted-foreground/60 mb-3">Average PnL when each rule was followed vs broken</p>

      {chartData.length === 0 ? (
        <div className="h-72 flex flex-col items-center justify-center text-center">
          <ShieldCheck className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No rule data yet.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Journal trades with this playbook to see rule stats.</p>
        </div>
      ) : (
        <div ref={containerRef} className="w-full">
          {dimensions.width > 0 && <AgCharts options={options} />}
        </div>
      )}
    </div>
  );
};

export default RuleAdherenceChart;
